import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode, PanAppState, PlatformService } from '@panamax/app-state';
import { MessageHandlerService } from 'src/app/shared/services/message/message-handler.service';
import {
  MyOrdersStoreService,
  OrderETAMessageType,
  OrderHeader,
  OrderStatus,
} from '@usf/ngrx-order';
import { combineLatest, filter, map, skip, take, tap } from 'rxjs';
import { OrderExceptionService } from '@order/pages/order-exceptions/service/order-exception.service';
import {
  DisplayType,
  OrderStatusDetails,
} from 'src/app/shared/constants/order-status-title.enum';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import {
  MyOrdersViewModel,
  OrderRow,
  PastOrdersRow,
  CustomerOrdersViewModel,
  MyOrdersRowType,
  ImportedOrdersViewModel,
} from '../../../shared/models/my-orders-view.model';
import { OrderService } from '../../services/order.service';
import { PanFeature } from 'src/environments/environment.features';
import { ModalController, PopoverController } from '@ionic/angular';
import { WmtPopoverComponent } from '../wmt-popover/wmt-popover.component';
import { Customer } from '@usf/customer-types';
import { MyOrdersTracingService } from 'src/app/my-orders/tracking/tracing/my-orders-tracing.service';
import { PATHS } from '../../../shared/constants/paths';
import { OrderTracingService } from '../../tracking/tracing/order-tracing.service';
import { OrderSubmittedAnalyticsService } from 'src/app/order/pages/order-submitted/tracking/analytics/order-submitted-analytics.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { OrderSyncingComponent } from '@shared/components/popovers/order-syncing/order-syncing.component';
import { FEATURES } from '@shared/constants/splitio-features';
import { isProntoOrder } from '@shared/helpers/pronto.helpers';
import { NoticeTooltipComponent } from '@shared/components/notice-tooltip/notice-tooltip.component';
import { OrderAnalyticsService } from '@order/tracking/analytics/order-analytics.service';

/**
 * Enum for the different navigation types after syncing modal
 */
export enum SyncingModalRouting {
  EXCEPTIONS = 'exceptions',
  INSIGHTS = 'insights',
  SUBMITTED = 'submitted',
  EDIT = 'edit',
  NEW = 'new',
}

@Component({
  template: '',
})
export class OrdersListComponent implements OnInit, AfterViewInit {
  @Input() ordersList:
    | MyOrdersViewModel
    | CustomerOrdersViewModel
    | ImportedOrdersViewModel;
  @Input() customerOrdersList: CustomerOrdersViewModel;
  @Input() pastOrders: boolean;
  @Output() quickViewClickEmitter: EventEmitter<any> = new EventEmitter();

  @ViewChildren('orders') ordersListComponents: any;

  displayType = DisplayType;
  wmtPopover: any;
  isInternalUser: boolean;
  orderStatus = OrderStatus;

  readonly PanFeature = PanFeature;
  readonly orderETAMessageType = OrderETAMessageType;
  readonly insightsOrderLinkFeatureFlag =
    FEATURES.split_global_order_insight.name;
  readonly dynamicCookbookOrderFeatureFlag =
    FEATURES.split_dynamic_order_cookbook.name;
  hideEtaGlobalFeatureFlag: boolean;
  isOnProntoDivisionFlag: boolean;

  constructor(
    private myOrderStoreService: MyOrdersStoreService,
    readonly orderService: OrderService,
    readonly router: Router,
    readonly platformService: PlatformService,
    private orderExceptionService: OrderExceptionService,
    private navigationHelperService: NavigationHelperService,
    private popoverController: PopoverController,
    private messageHandlerService: MessageHandlerService,
    protected panAppState: PanAppState,
    private myOrdersTracingService: MyOrdersTracingService,
    private orderTracingService: OrderTracingService,
    private orderSubmittedAnalyticsService: OrderSubmittedAnalyticsService,
    private modalService: ModalService,
    private modalController: ModalController,
  ) {}

  orderTypes = {
    RT: 'i18n.orderStatusPage.rTOrder',
    VS: 'i18n.orderStatusPage.vSOrder',
    WC: 'i18n.orderStatusPage.wCOrder',
    CC: 'i18n.orderStatusPage.cCarryOrder',
    RP: 'i18n.orderStatusPage.rPOrder',
  };

  ngOnInit(): void {
    combineLatest([
      this.panAppState.feature$([FEATURES.split_global_min_order_met.name]),
      this.panAppState.feature$([FEATURES.split_global_order_hide_eta.name]),
    ])
      .pipe(take(1))
      .subscribe(([isOnProntoDivisionFeatureFlag, hideEtaFeatureFlag]) => {
        this.isOnProntoDivisionFlag = isOnProntoDivisionFeatureFlag;
        this.hideEtaGlobalFeatureFlag = hideEtaFeatureFlag;
      });
  }

  ngAfterViewInit(): void {
    this.setIsInternalUser();
  }

  createNewOrder(orderRow: OrderRow): void {
    if (orderRow) {
      this.panAppState.customer$.pipe(take(1)).subscribe(customer => {
        this.orderService
          .shouldShowCartSyncingPopover$()
          .pipe(take(1))
          .subscribe(async showPopover => {
            if (showPopover) {
              await this.presentSyncingModal(
                '',
                orderRow,
                customer?.customerNumber,
                SyncingModalRouting.NEW,
              );
            } else {
              this.handleCreateNewOrder(
                orderRow,
                orderRow.customer?.customerNumber !== customer?.customerNumber,
              );
            }
          });
      });
    }
  }

  handleCreateNewOrder(orderRow: OrderRow, customerSwitch: boolean) {
    if (customerSwitch) {
      this.orderService.switchToCustomer(
        orderRow.customer,
        orderRow.orderHeader?.departmentNumber,
      );
      this.orderService.createNewOrderAfterCustomerRelatedDataLoaded(
        orderRow.customer,
        orderRow.orderHeader?.departmentNumber.toString(),
      );
    } else {
      this.orderService.createNewOrderAndNav();
    }
  }

  openOrder(orderRow: OrderRow, isCustomerOrderCard: boolean, event?: Event) {
    event?.stopPropagation();
    event?.preventDefault();
    this.orderTracingService.traceOrderCardClicked(
      orderRow.orderHeader?.orderId,
      orderRow.orderHeader?.origOrderId,
      orderRow.orderHeader?.origSplitOrderId,
      orderRow.orderHeader?.totalPrice,
      orderRow.orderHeader?.tandemOrderNumber,
      orderRow.orderHeader?.customerNumber,
      orderRow.orderHeader?.departmentNumber,
      orderRow.orderHeader?.divisionNumber,
      orderRow.orderHeader?.requestedDeliveryDate,
      orderRow.orderHeader?.confirmedDeliveryDate,
      isCustomerOrderCard,
    );
    const orderId =
      `${orderRow.orderHeader?.customerNumber}:` +
      `${orderRow.orderHeader?.departmentNumber}:` +
      `${orderRow.orderHeader?.orderId}`;

    const displayType =
      OrderStatusDetails[orderRow.orderHeader?.orderStatus]?.displayType;
    if (
      orderRow.orderHeader?.orderStatus === OrderStatus.IN_PROGRESS ||
      orderRow.orderHeader?.orderStatus === OrderStatus.ORDER_ERROR
    ) {
      this.orderService
        .shouldShowCartSyncingPopover$()
        .pipe(take(1))
        .subscribe(async showPopover => {
          if (showPopover) {
            this.panAppState.customer$
              .pipe(take(1))
              .subscribe(async customer => {
                await this.presentSyncingModal(
                  orderId,
                  orderRow,
                  customer?.customerNumber,
                  SyncingModalRouting.NEW,
                );
              });
          } else {
            this.navigateToOrderEditPage(orderId, orderRow.customer);
          }
        });
    } else if (
      !!displayType &&
      (displayType === DisplayType.SUBMITTED ||
        displayType === DisplayType.SUBMITTED_WITH_EXCEPTIONS ||
        displayType === DisplayType.ORDER_CANCELLED)
    ) {
      if (orderRow?.isTmUser) {
        this.orderTracingService.trackGetOrderDetailClick(
          orderRow.orderHeader?.orderId,
          orderRow.orderHeader?.tandemOrderNumber,
          orderRow.orderHeader?.totalPrice,
          orderRow.orderHeader?.requestedDeliveryDate,
          orderRow.orderHeader?.confirmedDeliveryDate,
        );
      }
      this.panAppState.customer$.pipe(take(1)).subscribe(customer => {
        if (customer.customerNumber === orderRow.orderHeader?.customerNumber) {
          this.navigationHelperService.routeToSubmittedOrder(orderId);
        } else {
          this.orderService
            .shouldShowCartSyncingPopover$()
            .pipe(take(1))
            .subscribe(async showPopover => {
              if (showPopover) {
                await this.presentSyncingModal(
                  orderId,
                  orderRow,
                  customer?.customerNumber,
                  SyncingModalRouting.SUBMITTED,
                );
              } else {
                this.navigationHelperService.routeToSubmittedOrder(orderId);
              }
            });
        }
      });
    }
  }

  editSubmittedOrder(orderRow: OrderRow, event: Event) {
    event.stopPropagation();

    this.orderService.trackEditOrderButtonClick(orderRow.orderHeader);

    if (orderRow.isEditable) {
      this.orderService
        .shouldShowCartSyncingPopover$()
        .pipe(take(1))
        .subscribe(showPopover => {
          if (showPopover) {
            this.panAppState.customer$
              .pipe(take(1))
              .subscribe(async customer => {
                await this.presentSyncingModal(
                  orderRow?.orderHeader?.orderId,
                  orderRow,
                  customer?.customerNumber,
                  SyncingModalRouting.EDIT,
                );
              });
          } else {
            this.handleEditableOrder(orderRow);
          }
        });
    }
  }

  handleEditableOrder(orderRow: OrderRow) {
    const fullOrderId =
      `${orderRow.orderHeader?.customerNumber}:` +
      `${orderRow.orderHeader?.departmentNumber}:` +
      `${orderRow.orderHeader?.orderId}`;
    if (orderRow?.isTmUser && orderRow?.orderHeader?.orderType !== 'VS') {
      this.handleTmUser(orderRow, true);
    } else {
      this.navigateToOrderEditPage(fullOrderId, orderRow.customer);
    }
  }

  handleTmUser(orderRow: OrderRow, isEditFlow: boolean) {
    if (
      orderRow?.orderHeader.customerNumber ===
        orderRow?.currentCustomerInState?.customerNumber &&
      orderRow?.orderHeader.departmentNumber?.toString() ===
        orderRow?.currentCustomerInState?.departmentNumber?.toString() &&
      orderRow?.orderHeader.divisionNumber ===
        orderRow?.currentCustomerInState?.divisionNumber
    ) {
      this.messageHandlerService.createSpinnerModal().then(() => {
        this.myOrderStoreService.retrieveOrderDetails(
          orderRow?.orderHeader?.tandemOrderNumber,
          isEditFlow,
        );
      });
    } else {
      this.messageHandlerService.createSpinnerModal().then(() => {
        const VMcustomerNumber = orderRow?.customer?.customerNumber;
        this.orderService.switchToCustomerIfNeeded(
          orderRow?.customer,
          orderRow?.orderHeader.departmentNumber,
        );
        this.panAppState.customer$
          .pipe(
            filter(
              updatedCustomer =>
                VMcustomerNumber === updatedCustomer?.customerNumber,
            ),
            take(1),
          )
          .subscribe(updatedCustomer => {
            this.myOrderStoreService.retrieveOrderDetails(
              orderRow?.orderHeader.tandemOrderNumber,
              isEditFlow,
            );
          });
      });
    }
  }

  navigateToOrderEditPage(orderId: string, customer: Customer) {
    this.myOrderStoreService
      .getOrderWithId(orderId)
      .pipe(take(1))
      .subscribe(order => {
        this.orderService.setLoading(true);
        const path = ['order'];
        const deviceTypePath = this.platformService?.isTouch.value
          ? path.unshift('touch')
          : path.unshift('desktop');
        this.router.navigate(path);
        this.orderService.makeOrderCurrentOrder(order, customer);
      });
  }

  resubmitOrder(orderRow: OrderRow, event: Event) {
    event.stopPropagation();
    const orderId =
      `${orderRow.orderHeader.customerNumber}:` +
      `${orderRow.orderHeader.departmentNumber}:` +
      `${orderRow.orderHeader.orderId}`;
    this.myOrderStoreService
      .getOrderWithId(orderId)
      .pipe(take(1))
      .subscribe(order => {
        this.orderService.makeOrderCurrentOrder(order, orderRow.customer);
        this.router.navigate([PATHS.ORDER]);
      });
  }

  resolveSubmittedOrder(orderRow: OrderRow, event: Event) {
    event.stopPropagation();
    if (
      orderRow.orderHeader.orderStatus === OrderStatus.SUBMITTED_WITH_EXCEPTIONS
    ) {
      const orderId = `${orderRow.orderHeader.customerNumber}:${orderRow.orderHeader.departmentNumber}:${orderRow.orderHeader.orderId}`;
      combineLatest([
        this.panAppState.currentUrl$,
        this.panAppState.customer$,
        this.orderService.shouldShowCartSyncingPopover$(),
      ])
        .pipe(
          take(1),
          map(([url, customer, showPopover]) => ({
            isOrderStatusPage: url?.split('/')[2] === 'my-orders',
            customer,
            showPopover,
          })),
          tap(({ isOrderStatusPage, customer, showPopover }) => {
            if (
              showPopover &&
              orderRow.orderHeader?.customerNumber !== customer?.customerNumber
            ) {
              this.presentSyncingModal(
                orderId,
                orderRow,
                customer?.customerNumber,
                SyncingModalRouting.EXCEPTIONS,
              );
            } else {
              this.myOrdersTracingService.traceViewExceptionsLinkClick(
                orderRow.orderHeader,
                isOrderStatusPage,
              );
              this.orderExceptionService.navigateToResolveExceptionsPage(
                orderRow.orderHeader,
              );
            }
          }),
        )
        .subscribe();
    }
  }

  openWMTPopover = async (orderRow: OrderRow, event, index = undefined) => {
    event.stopPropagation();
    if (!!index) {
      this.ordersListComponents.toArray()[index]?.nativeElement?.focus();
    }
    this.wmtPopover = await this.popoverController.create({
      component: WmtPopoverComponent,
      event,
      componentProps: {
        deliveryDetails: orderRow?.wmtDeliveryDate,
        orderHeaderDeliveryDetails: orderRow?.orderHeader,
      },
      cssClass:
        orderRow?.wmtDeliveryDate?.isDelivered ||
        orderRow?.orderHeader?.orderDelivered
          ? ['wmt-popover', 'delivered']
          : ['wmt-popover'],
      mode: 'ios',
    });
    await this.wmtPopover.present();
  };

  trackItem(index: any, item: any) {
    return item ? index : undefined;
  }

  isValidDate(dateToValidate: any): boolean {
    try {
      if (new Date(dateToValidate).toString() === 'Invalid Date') return false;
    } catch (e) {
      return false;
    }
    return true;
  }

  async presentSyncingModal(
    orderId: string,
    orderRow: OrderRow,
    currentCustomerNumber: number,
    navigationType?: SyncingModalRouting,
  ) {
    const isCustomerSwitch =
      orderRow.customer?.customerNumber !== currentCustomerNumber;

    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      OrderSyncingComponent,
      {
        customerSwitch: isCustomerSwitch,
        orderSwitch: true,
      },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then(resp => {
      if (resp?.data?.continueClick) {
        if (orderId === '') {
          this.handleCreateNewOrder(orderRow, isCustomerSwitch);
        } else if (navigationType === SyncingModalRouting.EXCEPTIONS) {
          this.myOrdersTracingService.traceViewExceptionsLinkClick(
            orderRow.orderHeader,
            false,
          );
          this.orderExceptionService.navigateToResolveExceptionsPage(
            orderRow.orderHeader,
          );
        } else if (navigationType === SyncingModalRouting.INSIGHTS) {
          this.navigationHelperService.routeToOrderInsights(orderId);
        } else if (navigationType === SyncingModalRouting.SUBMITTED) {
          this.navigationHelperService.routeToSubmittedOrder(orderId);
        } else if (navigationType === SyncingModalRouting.EDIT) {
          this.handleEditableOrder(orderRow);
        } else if (navigationType === SyncingModalRouting.NEW) {
          this.navigateToOrderEditPage(orderId, orderRow.customer);
        }
      }
    });
    return await modal.present();
  }

  openCopyOrderModalEmit(event: any, pastOrderRow: PastOrdersRow) {
    event.stopPropagation();
    this.quickViewClickEmitter.emit({ pastOrderRow });
  }

  insightsItems(item: OrderRow) {
    return (item?.orderHeader?.dyfProductsNotOrdered || []).concat(
      item?.orderHeader?.newProductsOrderedNotInRecentlyPurchased || [],
    );
  }

  openInsightsOrder(orderRow: OrderRow, event?: Event) {
    event.stopPropagation();
    const orderId = `${orderRow.orderHeader.customerNumber}:${orderRow.orderHeader.departmentNumber}:${orderRow.orderHeader.orderId}`;

    combineLatest([
      this.orderService.shouldShowCartSyncingPopover$(),
      this.panAppState.customer$,
    ])
      .pipe(take(1))
      .subscribe(([showPopover, customer]) => {
        if (
          showPopover &&
          orderRow.orderHeader?.customerNumber !== customer?.customerNumber
        ) {
          this.presentSyncingModal(
            orderId,
            orderRow,
            customer?.customerNumber,
            SyncingModalRouting.INSIGHTS,
          );
        } else {
          this.navigationHelperService.routeToOrderInsights(orderId);
        }
      });
  }

  setIsInternalUser(): void {
    this.panAppState?.accessToken$
      ?.pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          this.isInternalUser = decoded?.['usf-claims']?.userType === 'corp-ad';
        }
      });
  }

  //Hide modify link only on exception orders with order insights (to avoid displaying 3 action links)
  hideModifyLink(item: OrderRow, isOnline: boolean): boolean {
    return (
      item.displayType === DisplayType.SUBMITTED_WITH_EXCEPTIONS &&
      !item.restrictToOG &&
      item?.orderHeader?.orderType === 'RT' &&
      this.isInternalUser &&
      this.insightsItems(item)?.length > 0 &&
      isOnline
    );
  }

  getDisplayClass(item: OrderRow): string {
    let displayClass = '';
    switch (item.displayType) {
      case DisplayType.ORDER_ERROR:
      case DisplayType.SUBMITTED_WITH_EXCEPTIONS:
      case DisplayType.SUBMISSION_ERROR:
      case DisplayType.DELIVERY_ISSUE:
      case DisplayType.DELIVERY_DELAYED:
      case DisplayType.ORDER_CANCELLED:
        displayClass = 'order-error-red';
        break;
      case DisplayType.SUBMITTED:
      case DisplayType.OUT_FOR_DELIVERY:
      case DisplayType.QUANTITIES_UPDATED:
      case DisplayType.ATTEMPTED_DELIVERY:
      case DisplayType.DELIVERED:
      case DisplayType.NO_ORDER:
        displayClass = 'no-order-row';
        break;
      default:
        displayClass = '';
    }
    return displayClass;
  }

  formatOrderStatusViewed(item: OrderRow) {
    return item?.orderViewed
      ? { 'background-color': '#EBEBEB' }
      : { 'background-color': 'white' };
  }

  shouldDisplayResolveExceptionsLink(orderRow: OrderRow): boolean {
    return (
      orderRow?.displayType === DisplayType.SUBMITTED_WITH_EXCEPTIONS &&
      !orderRow?.restrictToOG &&
      orderRow?.orderHeader?.orderStatus !== OrderStatus.SHIPPED_WITH_EXCEPTIONS
    );
  }
  get rowType(): typeof MyOrdersRowType {
    return MyOrdersRowType;
  }

  getOrderHeaderTxt(orderHeader: OrderHeader): string {
    if (
      orderHeader?.orderType == 'RT' &&
      this.isOnProntoDivisionFlag &&
      isProntoOrder(orderHeader)
    ) {
      return this.orderTypes.RP;
    }
    return this.orderTypes[orderHeader.orderType];
  }

  async presentPopover(event: any, message?: string) {
    event.stopPropagation();
    const popover = await this.popoverController.create({
      component: NoticeTooltipComponent,
      event: event,
      translucent: true,
      mode: 'ios',
      alignment: 'center',
      cssClass: 'notice-message-tooltip',
      componentProps: {
        weatherAlert: message,
      },
    });
    return await popover.present();
  }

  displayInsightsLink(item: OrderRow): boolean {
    const insightsCount = this.insightsItems(item)?.length;
    if (item?.orderHeader?.orderType === 'RT') {
      if (insightsCount === 0) {
        return item?.orderHeader?.cookbookAdoptionPercent > 0;
      } else if (insightsCount > 0) {
        return true;
      }
    }
    return false;
  }

  displayInsightsBadge(item: OrderRow): boolean {
    return this.insightsItems(item)?.length > 0;
  }
}
